/* eslint-disable no-case-declarations */
import moment from 'moment';
import {
    User,
    Document,
    Event,
    PaymentInformation,
    Requirement,
    EmploymentInfoInterface,
    FormSpecLabels,
    BirthStatusType,
} from 'lib/entities';
import * as actionTypes from '../actions/userActionTypes';
import * as authActionTypes from '../actions/authActionTypes';
import * as underwritingRequirementUtils from '../../utils/underwritingRequirementUtil';

export type DocumentState = {
    value: Document;
    uploading: {pending: boolean};
    isUploaded: boolean;
    error?: Error | string;
};

export type UserState = Omit<User, 'documents' | 'dateOfBirth' | 'applicationSubmittedAt'> & {
    dateOfBirth: Date | null;
    applicationSubmittedAt: Date | null;
    documents: Record<string, DocumentState>;
    employmentDetail?: EmploymentInfoInterface;
    birthStatus?: BirthStatusType;
};

type Action = {
    type: string;
    message?: string;
    payload: any;
    callback?: Function | undefined;
};

// TODO: We don't have optional checks throughout so TS doesn't like Partial<User>
const initialUserState = {
    documents: {},
} as UserState;

function mergeUserWithState(user: User, state: UserState): UserState {
    return {
        ...state,
        ...user,
        applicationSubmittedAt: user.applicationSubmittedAt ? moment(user.applicationSubmittedAt).toDate() : null,
        dateOfBirth: user.dateOfBirth ? moment(user.dateOfBirth, 'YYYY-MM-DD').toDate() : null,
    };
}

// eslint-disable-next-line @typescript-eslint/default-param-last
const userReducer = (state: UserState = initialUserState, action: Action) => {
    switch (action.type) {
        case actionTypes.USER_ADD_USER:
            return mergeUserWithState(action.payload, state);
        default:
            return state;
    }
};

function reduceDocument(state: UserState[], action: Action, documentState: DocumentState) {
    const userIndex = state.findIndex((u) => {
        return u.underwritingRequirements?.some((r) => r.id === action.payload.requirementId);
    });

    if (userIndex === -1) return state;

    const selectedUser = {...state[userIndex]};
    const newState = [...state];

    newState[userIndex] = {
        ...selectedUser,
        documents: {
            ...selectedUser.documents,
            [action.payload.s3key]: documentState,
        },
    };

    return newState;
}

function reduceDocumentPending(state: UserState[], action: Action) {
    return reduceDocument(state, action, {
        value: action.payload,
        uploading: {pending: true},
        isUploaded: false,
    });
}

function reduceDocumentFailed(state: UserState[], action: Action) {
    return reduceDocument(state, action, {
        value: action.payload,
        uploading: {pending: false},
        error: action.message,
        isUploaded: false,
    });
}

function reduceDocumentAsync(state: UserState[], action: Action) {
    return reduceDocument(state, action, {
        value: action.payload,
        uploading: {pending: false},
        isUploaded: true,
    });
}

const users = (state: UserState[] = [], action: Action): UserState[] => {
    switch (action.type) {
        case authActionTypes.AUTH_AUTHENTICATE_SUCCESS:
            const newUser = userReducer(
                {...initialUserState},
                {type: actionTypes.USER_ADD_USER, payload: action.payload},
            );
            const existingUser = state.find((u) => u.id === newUser.id) || {};
            // TODO: user type is incorrect here
            const empPaperwork = action.payload.paperworks.find(
                (p: any) => p.formSpec.label === FormSpecLabels.EMPLOYMENT,
            );
            let employmentDetail = {};
            if (empPaperwork) {
                employmentDetail = {
                    id: empPaperwork.id,
                    ...JSON.parse(empPaperwork.completedPaperwork),
                } as EmploymentInfoInterface;
            }
            return [
                {
                    ...existingUser,
                    ...newUser,
                    employmentDetail,
                },
                ...state.filter((u) => u.id !== newUser.id),
            ] as any;
        case authActionTypes.AUTH_AUTHENTICATE_VIA_LOGINLESS_SUCCESS:
            const newUserLoginless = userReducer(
                {...initialUserState},
                {
                    type: actionTypes.USER_ADD_USER,
                    payload: action.payload.user,
                },
            );
            const existingUserLoginless = state.find((u) => u.id === newUser.id) || {};
            // TODO: user type is incorrect here
            return [
                {
                    ...existingUserLoginless,
                    ...newUserLoginless,
                },
                ...state.filter((u) => u.id !== newUser.id),
            ] as any;
        case actionTypes.USER_ADD_USER:
            return [...state, userReducer({...initialUserState}, action)];
        case actionTypes.USER_UPDATE_USER_ASYNC:
            const newUpdatedUserState = state.map((user) => {
                if (user.id === action.payload.id) {
                    const u = mergeUserWithState(action.payload as User, user);
                    return u;
                }
                return user;
            });
            if (action.callback != null) {
                action.callback();
            }
            // TODO: user type is incorrect here
            return newUpdatedUserState as any;
        case actionTypes.USER_UPDATE_IDENTIFICATION_ASYNC:
        case actionTypes.USER_CREATE_IDENTIFICATION_ASYNC:
            const newIdentificationState = state.map((user) => {
                if (user.id == action.payload.customer_id) {
                    const newUserData: UserState = {
                        ...user,
                    };
                    if (action.payload.firstIdentification != null) {
                        newUserData.firstIdentification = action.payload.firstIdentification;
                    }
                    if (action.payload.secondIdentification != null) {
                        newUserData.secondIdentification = action.payload.secondIdentification;
                    }
                    return newUserData;
                }
                return user;
            });
            if (action.callback != null) {
                action.callback();
            }
            return newIdentificationState;
        case actionTypes.USER_CREATE_ADVISOR_CALL_EVENT_ASYNC:
            const newEventState = state.map((user) => {
                if (user.id == action.payload.customer_id) {
                    const newUserData: UserState = {
                        ...user,
                    };
                    if (action.payload.date != null && action.payload.type != null) {
                        newUserData.advisorCallEvent = {
                            date: action.payload.date,
                            type: action.payload.type,
                        } as Event;
                    }
                    return newUserData;
                }
                return user;
            });
            if (action.callback != null) {
                action.callback();
            }
            return newEventState;
        case actionTypes.USER_CREATE_PAYMENT_INFORMATION_ASYNC:
            const newPaymentInformationState = state.map((user, i) => {
                if (i == 0) {
                    const newUserData: UserState = {
                        ...user,
                    };
                    newUserData.paymentInformation = {
                        label: action.payload.label,
                        accountNumber: action.payload.accountNumber,
                        routingNumber: action.payload.routingNumber,
                        paymentFrequency: action.payload.paymentFrequency,
                        dayOfMonth: action.payload.dayOfMonth,
                        plaidAccountId: action.payload.plaidAccountId,
                    } as PaymentInformation;
                    return newUserData;
                }
                return user;
            });
            if (action.callback != null) {
                action.callback();
            }
            return newPaymentInformationState;

        case actionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT_FAILED:
            if (action.callback != null) {
                action.callback('FAILED');
            }
            return state;

        case actionTypes.USER_UPDATE_UNDERWRITING_REQUIREMENT_ASYNC:
            const newUnderwritingRequirementState = state.map((user, i) => {
                if (i !== 0) return user;
                const requirementIndex = user.underwritingRequirements.findIndex((r) => r.id === action.payload.id);
                const requirement = user.underwritingRequirements[requirementIndex];
                return {
                    ...user,
                    underwritingRequirements: underwritingRequirementUtils.sortRequirements([
                        ...user.underwritingRequirements.slice(0, requirementIndex),
                        {
                            ...requirement,
                            status: action.payload.status,
                            data: action.payload.data,
                        },
                        ...user.underwritingRequirements.slice(requirementIndex + 1),
                    ]),
                };
            });
            if (action.callback != null) {
                action.callback();
            }
            return newUnderwritingRequirementState;
        case actionTypes.USER_CREATE_EMPLOYMENT_INFO_FAILED:
        case actionTypes.USER_UPDATE_EMPLOYMENT_INFO_FAILED:
            if (action.callback != null) {
                action.callback(action.message);
            }
            return state;
        case actionTypes.USER_UPDATE_EMPLOYMENT_INFO_ASYNC:
        case actionTypes.USER_CREATE_EMPLOYMENT_INFO_ASYNC:
            //TODO: Move underwriting requirements to separate reducer
            const newEmploymentInfoState = state.map((user, i) => {
                if (i == 0) {
                    const newUserData: UserState = {
                        ...user,
                    };

                    const employmentInfo = {
                        id: '',
                        insertedAt: '',
                        // Adding temporary updatedAt to prevent error in sortRequirement function without reload
                        updatedAt: Date(),
                        type: action.payload.type,
                        dataJson: action.payload.dataJson,
                        title: action.payload.title,
                        status: action.payload.status,
                    } as Requirement;

                    if (!Array.isArray(newUserData.underwritingRequirements)) {
                        //Requirements are null if none exist prior to this point
                        newUserData.underwritingRequirements = [employmentInfo];
                    } else {
                        newUserData.underwritingRequirements?.push(employmentInfo);
                    }

                    return newUserData;
                }
                return user;
            });
            if (action.callback != null) {
                action.callback();
            }
            return newEmploymentInfoState;
        case actionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT:
            return reduceDocumentPending(state, action);
        case actionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT_FAILED:
            return reduceDocumentFailed(state, action);
        case actionTypes.USER_UPDATE_UNDERWRITING_DOCUMENT_ASYNC:
            return reduceDocumentAsync(state, action);
        default:
            return state;
    }
};

export default users;
